import React, { FC, memo, useCallback, useEffect, useState } from 'react';

import { useApp } from 'App';
import { ResponseError } from 'utils/api';
import { interventionPlanAdminApi, interventionPlanIntervenerApi } from 'utils/api/ratings';
import { RatingResponse, RatingSummaryResponse } from 'utils/api/notice';
import { ActionTypes } from 'App/types';
import { notificationTypes } from 'utils/constants';
import { userRoles } from 'utils/userRoles';
import usePrefix from 'utils/usePrefix';

import Button from 'components/Button';
import RatingHistoryModal from 'components/Modals/RatingHistoryModal';
import RatingModal from 'components/Modals/RatingModal';

import {
  RatingStyled,
  RatingHeaderStyled,
  RatingHeaderActionsStyled,
  RatingBodyStyled,
  RatingNoteStyled,
} from './styles';
import RatingStars from './RatingStars';
import AverageRating from './AverageRating';

interface Props {
  noticeId: number;
  inAdminForm?: boolean;
}

const Rating: FC<Props> = ({ noticeId, inAdminForm }) => {
  const t = usePrefix('Rating');
  const [{ profile }, dispatch] = useApp();
  const isAdmin = profile?.role === userRoles.admin;

  const [currentRating, setCurrentRating] = useState<RatingResponse | null>(null);
  const [summary, setSummary] = useState<RatingSummaryResponse | null>(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const loadAdmin = useCallback(async () => {
    try {
      const response = await interventionPlanAdminApi.getRatings(noticeId);
      setSummary(response);
    } catch (error) {
      const typedError = error as ResponseError;
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
      });
    }
  }, [noticeId, dispatch]);

  const loadIntervener = useCallback(async () => {
    try {
      const response = await interventionPlanIntervenerApi.getMyRating(noticeId);
      setCurrentRating(response);
    } catch (error) {
      const typedError = error as ResponseError;
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
      });
    }
  }, [noticeId, dispatch]);

  useEffect(() => {
    if (isAdmin) {
      loadAdmin();
    } else {
      loadIntervener();
    }
  }, [isAdmin, loadAdmin, loadIntervener]);

  const onEdit = useCallback(() => setIsEdit(true), []);
  const onCloseEdit = useCallback(() => setIsEdit(false), []);
  const onDetails = useCallback(() => setIsDetailsOpen(true), []);
  const onCloseDetails = useCallback(() => setIsDetailsOpen(false), []);

  const onSaved = useCallback((response: RatingResponse) => {
    setCurrentRating(response);
    setIsEdit(false);
  }, []);

  const hasRating = !!currentRating;

  return (
    <RatingStyled $inAdminForm={inAdminForm}>
      <RatingHeaderStyled>
        <h3>{t('title_intervention_plan')}</h3>
        <RatingHeaderActionsStyled>
          {isAdmin ? (
            <Button label={t('cta_show_rating_details')} color="link" onClick={onDetails} />
          ) : (
            <Button
              label={hasRating ? t('cta_edit_rating') : t('addRating')}
              color="link"
              onClick={onEdit}
              noPadding
            />
          )}
        </RatingHeaderActionsStyled>
      </RatingHeaderStyled>

      <RatingBodyStyled>
        {isAdmin ? (
          <AverageRating average={summary?.average_stars ?? 0} count={summary?.ratings.length ?? 0} />
        ) : (
          <RatingStars rating={currentRating?.stars ?? 0} />
        )}
        {currentRating?.comment && !isAdmin ? (
          <RatingNoteStyled><p>{currentRating.comment}</p></RatingNoteStyled>
        ) : null}
      </RatingBodyStyled>

      {!isAdmin && (
        <RatingModal
          noticeId={noticeId}
          isOpen={isEdit}
          onClose={onCloseEdit}
          onSaved={onSaved}
          initialRating={currentRating?.stars}
          initialNote={currentRating?.comment ?? ''}
          hasRating={hasRating}
        />
      )}
      {isAdmin && (
        <RatingHistoryModal
          isOpen={isDetailsOpen}
          onClose={onCloseDetails}
          ratings={summary?.ratings ?? []}
        />
      )}
    </RatingStyled>
  );
};

export default memo(Rating);
